/**
 * useComparisonHistory Hook
 *
 * Fetches rating history for comparison institutions and transforms
 * to HistoricalPeriod format for the TimelineChart.
 */

import { useState, useCallback, useRef } from 'react';
import { getRatingHistory } from '../services/institutionService';
import type { RatingHistoryEntry } from '../types/institution';
import type { HistoricalPeriod, CAMELComponent } from '../pages/InstitutionDashboard/types';
import type { CAMELCode } from '../constants/camelConstants';

interface ComparisonData {
  institutionId: string;
  institutionName: string;
  history: HistoricalPeriod[];
  isLoading: boolean;
  error: string | null;
}

interface UseComparisonHistoryResult {
  data: Map<string, ComparisonData>;
  isLoading: boolean;
  fetchHistory: (institutionId: string, institutionName: string) => void;
  removeHistory: (institutionId: string) => void;
  clearAll: () => void;
}

// Map component_scores keys to CAMELCode
const COMPONENT_KEY_TO_CODE: Record<string, CAMELCode> = {
  capital: 'C',
  liquidity: 'L',
  quality: 'S', // Asset quality maps to S (Solvencia)
  efficiency: 'EG',
  profitability: 'R',
  interest_rate_risk: 'STI',
  // Alternative keys
  solvencia: 'S',
  rentabilidad: 'R',
  gestion: 'EG',
};

/**
 * Transform RatingHistoryEntry to HistoricalPeriod format
 */
function transformToHistoricalPeriod(entry: RatingHistoryEntry): HistoricalPeriod {
  const camelComponents: CAMELComponent[] = entry.component_scores
    ? Object.entries(entry.component_scores)
        .map(([key, value]) => {
          const code = COMPONENT_KEY_TO_CODE[key.toLowerCase()];
          if (!code) return null;
          return {
            code,
            score: value,
          };
        })
        .filter((c): c is CAMELComponent => c !== null)
    : [];

  return {
    date: entry.period,
    rating: entry.rating_category,
    finalScore: entry.numeric_score,
    camel: camelComponents,
  };
}

export function useComparisonHistory(): UseComparisonHistoryResult {
  const [data, setData] = useState<Map<string, ComparisonData>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Map<string, HistoricalPeriod[]>>(new Map());

  const fetchHistory = useCallback(async (institutionId: string, institutionName: string) => {
    // Check cache first
    if (cacheRef.current.has(institutionId)) {
      setData(prev => {
        const next = new Map(prev);
        next.set(institutionId, {
          institutionId,
          institutionName,
          history: cacheRef.current.get(institutionId)!,
          isLoading: false,
          error: null,
        });
        return next;
      });
      return;
    }

    // Set loading state
    setData(prev => {
      const next = new Map(prev);
      next.set(institutionId, {
        institutionId,
        institutionName,
        history: [],
        isLoading: true,
        error: null,
      });
      return next;
    });

    setIsLoading(true);

    try {
      const ratings = await getRatingHistory(institutionId);
      const history = ratings.map(transformToHistoricalPeriod);

      // Cache the result
      cacheRef.current.set(institutionId, history);

      setData(prev => {
        const next = new Map(prev);
        next.set(institutionId, {
          institutionId,
          institutionName,
          history,
          isLoading: false,
          error: null,
        });
        return next;
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch history';
      setData(prev => {
        const next = new Map(prev);
        next.set(institutionId, {
          institutionId,
          institutionName,
          history: [],
          isLoading: false,
          error: errorMessage,
        });
        return next;
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeHistory = useCallback((institutionId: string) => {
    setData(prev => {
      const next = new Map(prev);
      next.delete(institutionId);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setData(new Map());
  }, []);

  return {
    data,
    isLoading,
    fetchHistory,
    removeHistory,
    clearAll,
  };
}

export default useComparisonHistory;
